import {ClientRect, UniqueIdentifier} from '@dnd-kit/core';

import {prop} from 'ramda';

import {DashboardTileId} from '../types/DashboardTileId';
import {DashboardTileSize} from '../types/DashboardTileSize';
import {PositionedDashboardGridTile} from './allocateDashboardGrid';
import {DASHBOARD_TILE_SIZES} from './dashboardTileSizes';
import {getDashboardStructuralBand} from './getDashboardStructuralBand';
import {isDashboardTileFullWidth} from './isDashboardTileFullWidth';

export type TileDropPlacement = 'before' | 'after' | 'swap';

interface TileDropPoint {
  x: number;
  y: number;
}

interface TileDropPlacementOptions {
  activeSize: DashboardTileSize;
  overId: DashboardTileId;
  tiles: PositionedDashboardGridTile[];
  rects: ReadonlyMap<UniqueIdentifier, ClientRect>;
  overRect: ClientRect;
  point: TileDropPoint;
}

const getStructuralBandMidpoint = (options: TileDropPlacementOptions) => {
  const band = getDashboardStructuralBand(options.tiles, options.overId);

  if (band === null) {
    return options.overRect.top + options.overRect.height / 2;
  }

  const rects = options.tiles
    .filter((tile) => {
      const tileRows = prop(tile.size, DASHBOARD_TILE_SIZES).rows;

      return tile.row < band.endRow && tile.row + tileRows > band.startRow;
    })
    .flatMap((tile) => {
      const rect = options.rects.get(tile.id);

      return rect === undefined ? [] : [rect];
    });

  if (rects.length === 0) {
    return options.overRect.top + options.overRect.height / 2;
  }

  const top = Math.min(...rects.map((rect) => rect.top));
  const bottom = Math.max(...rects.map((rect) => rect.bottom));

  return top + (bottom - top) / 2;
};

export function resolveTileDropPlacement(options: TileDropPlacementOptions): TileDropPlacement {
  if (!isDashboardTileFullWidth(options.activeSize)) {
    return 'swap';
  }

  return options.point.y > getStructuralBandMidpoint(options) ? 'after' : 'before';
}
